import { Button, Modal, Group, Stack } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { primaryColor } from '../../../../constants/theme';
import { useMutation } from '@tanstack/react-query';
import { useApp } from '../../../../context/AppContext';
import { declineBooking } from '../../../../services/BookingService';
import {
  BookingResponseModel,
  RejectBookingInputModel,
} from '../../../../types/BookingModels';
import { ErrorModal } from '../error/ErrorModal';
import { BookingPreview } from './BookingPreview';

interface DeclineBookingProps {
  refreshBookings: () => void;
  profileId: string;
  groupedBookings: BookingResponseModel[];
  reloadOnSuccess?: boolean;
}

export const DeclineBooking: React.FC<DeclineBookingProps> = ({
  refreshBookings,
  profileId,
  groupedBookings,
  reloadOnSuccess,
}) => {
  const booking = groupedBookings[0];
  const { token } = useApp();
  const [opened, setOpened] = useState(false);
  const isProfessional = profileId === booking?.professionalId;
  const isStartInFuture = new Date(booking.startIso) > new Date();

  const {
    mutate: declineBookingMutation,
    isPending,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      const declineBookingInput: RejectBookingInputModel = {
        bookingIds: groupedBookings.map((b) => b.id),
      };
      await declineBooking(declineBookingInput, token || '');
    },
    onSuccess: () => {
      refreshBookings();
      setOpened(false);
      if (reloadOnSuccess) {
        window.location.reload();
      }
    },
    onError: (error) => {
      console.error('Error rejecting booking:', error);
    },
  });

  if (
    !isProfessional ||
    !isStartInFuture ||
    booking.accepted ||
    booking.isCancelled
  )
    return null;

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage="There was an error rejecting the booking. Please try again."
        dismissAction={() => reset()}
        retryAction={() => declineBookingMutation()}
        isLoading={isPending}
      />
      <Button
        radius="md"
        variant="outline"
        color={primaryColor}
        leftSection={<IconX size={16} />}
        onClick={() => setOpened(true)}
        data-testid={`decline-button-${booking.id}`}
      >
        Reject
      </Button>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton={false}
      >
        <Stack gap={10}>
          <Stack justify="center" align="center" gap={10}>
            <BookingPreview
              title="Reject Booking?"
              groupedBookings={groupedBookings}
              bottomText="Please note the booking will be automatically removed from your bookings page."
              titleTestId="decline-booking-modal-title"
              textTestId="decline-booking-modal-text"
            />
          </Stack>
          <Group gap={10} justify="center">
            <Button
              variant="outline"
              w="48%"
              color={primaryColor}
              onClick={() => setOpened(false)}
              data-testid="decline-modal-cancel-cta"
            >
              Cancel
            </Button>
            <Button
              w="48%"
              color={primaryColor}
              onClick={() => declineBookingMutation()}
              loading={isPending}
              data-testid="decline-modal-decline-cta"
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
